/**
 * The umpire page's vocabulary — translation, and one borrowed judgement.
 *
 * The judgement is §B10's, and it is deliberately **not** made here: which umpires may take a
 * slot is decided by `umpireBlockedReason` in `@hockeytd/shared`, the same function the draft's
 * own candidate filter reads. That is the whole point of it existing there —
 *
 *   > *"The draft's candidate filter and Run 34's dropdown read the same function, so a name
 *   > the engine will not propose and a name the picker greys out can never disagree — which
 *   > is exactly how a TD comes to believe the engine has overlooked somebody."*
 *
 * — so this module's job is to assemble the context that function needs out of the wire shapes
 * the page holds, and nothing more. There is no second copy of the four reasons on this side,
 * and no place a fifth could be invented.
 */

import {
  DEFAULT_UMPIRE_CONFIG,
  isOnFieldSlot,
  umpireBlockedReason,
  type EngineFixture,
  type EngineUmpire,
  type UmpireBlockedReason,
  type UmpireSlot,
} from "@hockeytd/shared";

import type { OfficialView, UmpireFixtureBody, UmpireSlotBody } from "../../api/types.js";
import { slotsOf, type UmpireDataset } from "./derive.js";

/** §B9's two status pills, and the third §B4 draws on a violating row. */
export const AI_DRAFTED = "AI Drafted";
export const TD_MODIFIED = "TD Modified";

/** The engine's narrow view of a fixture, assembled from the wire body. */
export function toEngineFixture(fixture: UmpireFixtureBody): EngineFixture {
  return {
    tms_match_db_id: fixture.tmsMatchDbId,
    match_no: fixture.matchNo,
    competition: fixture.competitionGender,
    date: fixture.date,
    time: fixture.time,
    stage: fixture.stage,
    home_team: fixture.homeTeamCode,
    away_team: fixture.awayTeamCode,
  };
}

/** The engine's narrow view of an umpire, assembled from the roster row. */
export function toEngineUmpire(official: OfficialView): EngineUmpire {
  return {
    tms_person_id: official.tmsPersonId,
    surname: official.surnames.toUpperCase(),
    country: official.country,
    role: official.role,
    panel: official.panel,
    avoid_vu: official.avoidVu,
    // Passed through EXACTLY as the roster holds it, null included: an unstated gender is not
    // a claim about anybody, and the shared engine treats it as unrestricted rather than
    // guessing. Defaulting it here would be this side inventing an answer the server did not
    // give — and it would grey out half the panel in the picker while the draft went on
    // proposing them, which is precisely the disagreement this module exists to prevent.
    gender: official.gender,
    cross_gender: official.crossGender,
    umpire_type: official.umpireType,
  };
}

/**
 * One line of §B10's list.
 *
 * `reason` is exactly one of the four strings the shared engine returns, or null. A blocked
 * option is `disabled` and stays on the list: §B10 says the chip is shown rather than the name
 * hidden, and an umpire who has simply vanished from the roster is the one thing a TD reading
 * this popover would misread as a bug.
 */
export interface UmpirePickerOption {
  official: OfficialView;
  reason: UmpireBlockedReason | null;
}

/**
 * §B10's list, for one slot of one fixture, measured against ONE dataset.
 *
 * The dataset is a parameter and always `aiReference` in this build, because §B9 makes the
 * advisory half the only editable one. Passing it rather than assuming it is what keeps the
 * function from being reachable for the Umpires Managers' half by a later edit that forgot
 * Part C invariant 5.
 *
 * **The thresholds are the engine's defaults, and the server holds the tournament's own.**
 * `umpire_onfield_per_day` and `umpire_onfield_consecutive_days` live in Configuration §06 and
 * are applied by the API on every write; this list only needs to be right about what it greys
 * out before the TD presses. §B4's panel foot and §06's rule text both put the first at one,
 * and migration 042 puts the second at three.
 *
 * **`onFieldDates` is scoped to what is on screen**, for the same reason and with the same
 * consequence. The page holds one day or one competition at a time, so a run of days that began
 * before the loaded scope is invisible here and the chip may not appear where the server's own
 * draft would have refused the seat. That is the right way round: the picker greying out a name
 * the API would then accept is a worse failure than the reverse, because the API is what
 * actually decides. The server measures over the whole schedule on every write.
 */
export function umpirePickerOptions(
  fixture: UmpireFixtureBody,
  slot: UmpireSlot,
  officials: readonly OfficialView[],
  allFixtures: readonly UmpireFixtureBody[],
  dataset: UmpireDataset = "aiReference",
): UmpirePickerOption[] {
  const engineFixture = toEngineFixture(fixture);

  // Everybody already holding a slot in THIS match — this cell's own holder excepted, since
  // re-picking the umpire already in the cell changes nothing and must not be refused.
  const membersOfMatch = slotsOf(fixture, dataset)
    .filter((entry) => entry.slot !== slot && entry.tmsPersonId !== null)
    .map((entry) => entry.tmsPersonId as string);

  // On-field duties already held on this date, across BOTH competitions (§B5 rule 3), with
  // this cell excluded for the same reason.
  const onFieldToday = new Map<string, number>();
  // Every DATE on which each umpire already holds on-field duty — what the consecutive-days
  // limit is measured over. Built from the same walk, over every fixture rather than only
  // today's, and with this cell excluded for the same reason again: an umpire already in the
  // cell must not be greyed out of the cell they are in.
  const onFieldDates = new Map<string, string[]>();
  for (const other of allFixtures) {
    for (const entry of slotsOf(other, dataset)) {
      if (entry.tmsPersonId === null) continue;
      if (other.tmsMatchDbId === fixture.tmsMatchDbId && entry.slot === slot) continue;
      if (!isOnFieldSlot(entry.slot)) continue;
      const held = onFieldDates.get(entry.tmsPersonId) ?? [];
      held.push(other.date);
      onFieldDates.set(entry.tmsPersonId, held);
      if (other.date !== fixture.date) continue;
      onFieldToday.set(entry.tmsPersonId, (onFieldToday.get(entry.tmsPersonId) ?? 0) + 1);
    }
  }

  /**
   * The umpire already seated in the OTHER on-field slot of this match, if any — the partner the
   * pairing rule judges each candidate against.
   *
   * Null for RU and VU, which are not part of the pair, and null while the other seat is still
   * empty: a pair the rules forbid cannot exist until there are two people in it, and greying
   * names out before the first of the two is chosen would refuse an arrangement nobody has made.
   */
  const partnerSlot = slot === "U1" ? "U2" : slot === "U2" ? "U1" : null;
  const partnerId =
    partnerSlot === null
      ? null
      : (slotsOf(fixture, dataset).find((entry) => entry.slot === partnerSlot)?.tmsPersonId ??
        null);
  const partner =
    partnerId === null
      ? null
      : (officials.find((official) => official.tmsPersonId === partnerId) ?? null);

  // The panel, mapped ONCE rather than per candidate: the partner-feasibility test scans it for
  // every umpire on the list, and mapping it inside that scan would be the same work squared.
  const enginePanel = officials.map(toEngineUmpire);

  return officials
    .filter((official) => official.role === "Umpire")
    .map((official) => ({
      official,
      reason: blockedReasonFor(official),
    }))
    .sort((a, b) =>
      a.official.mediaName < b.official.mediaName
        ? -1
        : a.official.mediaName > b.official.mediaName
          ? 1
          : 0,
    );

  function blockedReasonFor(official: OfficialView): UmpireBlockedReason | null {
    return umpireBlockedReason(toEngineUmpire(official), {
      fixture: engineFixture,
      slot,
      membersOfMatch,
      onFieldToday: onFieldToday.get(official.tmsPersonId) ?? 0,
      onFieldPartner: partner === null ? null : toEngineUmpire(partner),
      onFieldDates: onFieldDates.get(official.tmsPersonId) ?? [],
      panel: enginePanel,
      config: DEFAULT_UMPIRE_CONFIG,
    });
  }
}

/**
 * Re-exported so a component on this side reaches the shared definition through this module
 * and never imports a second thing that looks like it.
 */
export { umpireBlockedReason };

/** Whether this cell currently breaks one of §B5's two violation rules. */
export function hasViolation(slot: UmpireSlotBody): boolean {
  return slot.tmsPersonId !== null && slot.violation !== null;
}

/** The avatar's initials — first and last of the printed name, as the mockup composes them. */
export function initialsOf(mediaName: string): string {
  const parts = mediaName.trim().split(/\s+/);
  const first = parts[0]?.charAt(0) ?? "";
  const last = parts.length > 1 ? (parts[parts.length - 1]?.charAt(0) ?? "") : "";
  return `${first}${last}`.toUpperCase();
}
